"use client";

import React from "react";

import { useTransform, type MotionValue } from "framer-motion";
import type { PortableTextBlock } from "sanity";

import { cn } from "@/lib/utils";
import { MotionSpan } from "@/components/global/utils/motionTags";

/**
 * Portable text rendered one word at a time, each word brightening from
 * `dimmed` to full as the given progress passes it — the statement reads itself
 * in as you scroll rather than sitting half-muted on the page.
 *
 * Only what the statement uses is honoured: paragraphs, and the `em` mark for the
 * green pixel highlight. Any other mark is rendered as plain text, so nothing an
 * editor picks can pull a run out of the reveal.
 */

/**
 * Fraction of the progress each word's own fade occupies. Words are handed
 * equal starting points across the remainder, so a window this wide keeps a
 * handful of them mid-fade at once — a sweep across the line rather than a
 * word flicking on at a time.
 */
const WORD_WINDOW = 0.2;

const DIMMED = 0.4;

type Word = { text: string; em: boolean };

/** The one child shape the statement carries; anything else is skipped. */
type Span = { _type: string; text?: string; marks?: string[] };

/** Split every span into words, keeping the mark that matters for each. */
function toWords(block: PortableTextBlock): Word[] {
  const words: Word[] = [];
  const children = (block.children ?? []) as Span[];
  for (const child of children) {
    if (child._type !== "span" || !child.text) continue;
    const em = child.marks?.includes("em") ?? false;
    for (const text of child.text.split(/\s+/)) {
      if (text) words.push({ text, em });
    }
  }
  return words;
}

function WordSpan({
  word,
  index,
  total,
  progress,
}: {
  word: Word;
  index: number;
  total: number;
  progress: MotionValue<number>;
}) {
  const start = total <= 1 ? 0 : (index / (total - 1)) * (1 - WORD_WINDOW);
  const opacity = useTransform(
    progress,
    [start, start + WORD_WINDOW],
    [DIMMED, 1],
  );

  return <MotionSpan style={{ opacity }}>{word.text}</MotionSpan>;
}

/**
 * Consecutive words with the same mark share one `em`, so the highlight bar
 * runs unbroken under the whole lead-in instead of being cut at every space.
 */
function groupByMark(words: Word[]) {
  const groups: { em: boolean; words: { word: Word; index: number }[] }[] = [];
  words.forEach((word, index) => {
    const last = groups[groups.length - 1];
    if (last && last.em === word.em) last.words.push({ word, index });
    else groups.push({ em: word.em, words: [{ word, index }] });
  });
  return groups;
}

export default function RevealText({
  value,
  progress,
  className,
}: {
  value: PortableTextBlock[];
  progress: MotionValue<number>;
  className?: string;
}) {
  // each paragraph with the index of its first word, so a word's place in the
  // sweep is its place in the whole statement rather than in its own line
  const paragraphs = React.useMemo(
    () =>
      value
        .map(toWords)
        .filter((words) => words.length > 0)
        .reduce<{ words: Word[]; base: number }[]>((acc, words) => {
          const last = acc[acc.length - 1];
          const base = last ? last.base + last.words.length : 0;
          return [...acc, { words, base }];
        }, []),
    [value],
  );
  const total = paragraphs.reduce((sum, { words }) => sum + words.length, 0);

  return (
    <div className={cn("richtext richtext-start", className)}>
      {paragraphs.map(({ words, base }, p) => {
        return (
          <p key={p}>
            {groupByMark(words).map((group, g) => {
              const spans = group.words.map(({ word, index }, i) => (
                <React.Fragment key={index}>
                  {i > 0 && " "}
                  <WordSpan
                    word={word}
                    index={base + index}
                    total={total}
                    progress={progress}
                  />
                </React.Fragment>
              ));

              return (
                <React.Fragment key={g}>
                  {g > 0 && " "}
                  {group.em ? <em>{spans}</em> : spans}
                </React.Fragment>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}
